/**
 * StockCard Component (Phase 5 - Task 5.4)
 *
 * AI 추천 종목 카드 컴포넌트
 */
import React from 'react';
import type { AIStockData } from '../types/watchlist';
import { getRiskLevel, SignalItem, VerificationBadges, InfoSection } from './StockCardHelpers';
import './StockCard.css';

interface StockCardProps {
  stock: AIStockData;
  onExecute?: (ticker: string) => void;
  executing?: boolean;
  variant?: 'recommendation' | 'holding';
}

const StockCard: React.FC<StockCardProps> = ({
  stock,
  onExecute,
  executing = false,
  variant = 'recommendation',
}) => {
  const {
    ticker, sector, confidence, risk_level, catalyst, market_summary, why_now, source_hint,
    catalyst_label, confidence_label, risk_label: metricRiskLabel, screening_metadata_valid,
    strategy_signals, alpaca_valid, alpaca_name, alpaca_exchange,
    catalyst_verified, catalyst_confidence, catalyst_sentiment,
    signal_status, signal_error, strategy_strength,
  } = stock;

  const confidencePercent = confidence * 10;
  const { label: riskLabel } = getRiskLevel(risk_level);
  const actionClass = strategy_signals?.combined_action.toLowerCase() || 'hold';
  const signals = strategy_signals && strategy_signals.MA && strategy_signals.BB
    ? strategy_signals
    : null;
  const sourceHintText = variant === 'recommendation' && source_hint?.trim() !== 'Finnhub 뉴스'
    ? source_hint?.trim() || ''
    : '';
  const catalystLabel = catalyst_label || (variant === 'holding' ? '보유 현황' : '추천 사유');
  const showAiMetrics = screening_metadata_valid !== false;
  const strategySummary = signals ? getStrategySummary(signals, strategy_strength) : '';

  return (
    <div className={`stock-card ${actionClass} ${variant}`}>
      {/* 헤더 */}
      <div className="stock-header">
        <div className="stock-ticker">{ticker}</div>
        <div className="stock-sector">{sector}</div>
        <div className={`action-badge ${actionClass}`}>
          {signal_status === 'loading' ? '...' : strategy_signals?.combined_action || 'HOLD'}
        </div>
      </div>

      {/* AI 메트릭 */}
      {showAiMetrics && (
        <div className="ai-metrics">
          <div className="metric">
            <span className="label">{confidence_label || '신뢰도'}</span>
            <span className="value confidence">{confidencePercent}%</span>
          </div>
          <div className="metric">
            <span className="label">{metricRiskLabel || '리스크'}</span>
            <span className="value risk">{riskLabel}</span>
          </div>
        </div>
      )}

      {/* 검증 배지 */}
      <VerificationBadges
        alpacaValid={alpaca_valid} alpacaName={alpaca_name} alpacaExchange={alpaca_exchange}
        catalystVerified={catalyst_verified} catalystConfidence={catalyst_confidence}
        catalystSentiment={catalyst_sentiment} ticker={ticker}
      />

      {/* 정보 섹션 */}
      {catalyst && <InfoSection label={catalystLabel} text={catalyst} className="catalyst" />}
      {variant === 'recommendation' && market_summary && (
        <InfoSection label="시장 트렌드" text={market_summary} className="market-summary" />
      )}
      {variant === 'recommendation' && why_now && (
        <InfoSection label="왜 지금?" text={why_now} className="why-now" />
      )}
      {sourceHintText && <InfoSection label="뉴스 출처" text={sourceHintText} className="source-hint" />}

      {/* 전략 신호 (2행 레이아웃 - Phase 6: 7개 전략) */}
      {signal_status === 'loading' && !signals && (
        <div className="signal-state signal-loading">전략 신호 조회 중...</div>
      )}
      {signal_status === 'error' && (
        <div className="signal-state signal-error" title={signal_error}>
          전략 신호 조회 실패
        </div>
      )}
      {signals && (
        <div className="strategy-signals-compact">
          <span className="signals-label">Signals</span>
          <div className="signal-row">
            <SignalItem name="MA" signal={signals.MA?.signal ?? 0} />
            <span className="signal-divider">|</span>
            <SignalItem name="RSI" signal={signals.RSI?.signal ?? 0} />
            <span className="signal-divider">|</span>
            <SignalItem name="MACD" signal={signals.MACD?.signal ?? 0} />
            <span className="signal-divider">|</span>
            <SignalItem name="BB" signal={signals.BB?.signal ?? 0} />
          </div>
          <div className="signal-row">
            <SignalItem name="VOL" signal={signals.VOL?.signal ?? 0} />
            <span className="signal-divider">|</span>
            <SignalItem name="S/R" signal={signals.SR?.signal ?? 0} />
            <span className="signal-divider">|</span>
            <SignalItem name="PATTERN" signal={signals.PATTERN?.signal ?? 0} />
            <span className="signal-arrow">→</span>
            <span className={`combined-action-inline ${actionClass}`}>{signals.combined_action}</span>
          </div>
          {strategySummary && <div className="strategy-summary">{strategySummary}</div>}
        </div>
      )}

      {/* 수동 실행 버튼 */}
      {onExecute && (
        <button className="execute-btn" onClick={() => onExecute(ticker)} disabled={executing}>
          {executing ? '실행 중...' : '수동 실행'}
        </button>
      )}
    </div>
  );
};

function getStrategySummary(
  signals: NonNullable<AIStockData['strategy_signals']>,
  strategyStrength?: number,
): string {
  const values = [
    signals.MA?.signal ?? 0,
    signals.RSI?.signal ?? 0,
    signals.MACD?.signal ?? 0,
    signals.BB?.signal ?? 0,
    signals.VOL?.signal ?? 0,
    signals.SR?.signal ?? 0,
    signals.PATTERN?.signal ?? 0,
  ];
  const buyCount = values.filter((signal) => signal > 0).length;
  const sellCount = values.filter((signal) => signal < 0).length;
  const holdCount = values.length - buyCount - sellCount;
  const score = typeof strategyStrength === 'number'
    ? `전략 점수 ${Math.round(strategyStrength * 100)}% · `
    : '';
  return `${score}BUY ${buyCount} / SELL ${sellCount} / HOLD ${holdCount}`;
}

export default StockCard;
